import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../lib/useAuth";
import { useClientProjects } from "../../lib/useClientProjects";
import styles from "./ClientDashboard.module.css";

/* Etiquetas de estado tentativas — ajustar a los valores reales del sistema
   de control de Aya cuando se defina la estructura. */
const STATUS_LABEL = {
  in_development: "En desarrollo",
  in_progress: "En progreso",
  delivered: "Entregado",
  in_service: "En servicio",
  paused: "En pausa",
  // Estados de cotización que envía CGO
  pendiente_oc: "Pendiente de OC",
  aprobada: "Aprobado",
  en_proceso: "En proceso",
  pendiente_factura: "Por facturar",
  completado: "Completado",
};

/* Lectura tolerante de campos: aún no conocemos los nombres definitivos. */
function readProject(p) {
  const title = p.name || p.title || p.projectName || p.nombre || "Proyecto";
  const status = p.status || p.estado || null;
  const rawDescription =
    p.description || p.descripcion || p.shortDescription || p.resumen || "";
  // Sin nombre de proyecto, CGO usa la descripción como título: no repetirla.
  const description = rawDescription.trim() === title.trim() ? "" : rawDescription;
  const rawProgress = p.progress ?? p.avance ?? p.percent ?? null;
  const progress =
    rawProgress == null ? null : Math.max(0, Math.min(100, Number(rawProgress)));
  const code = p.code || p.codigo || (p.id ? p.id.slice(0, 6).toUpperCase() : "");
  const quotationPdfUrl = p.quotationPdfUrl || null;
  const ocPdfUrl = p.ocPdfUrl || null;
  const invoicePdfUrl = p.invoicePdfUrl || null;
  const projectDocuments = p.projectDocuments || [];
  let projectStartDate = p.projectStartDate || p.operationsData?.projectStartDate || null;
  let projectEndDate = p.projectEndDate || p.operationsData?.projectEndDate || null;
  const tasks = p.operationsData?.tasks || p.tasks || [];
  const milestones = Array.isArray(p.milestones) ? p.milestones : [];

  if (tasks && tasks.length > 0) {
    if (!projectStartDate) {
      const dates = tasks.map(t => t.startDate).filter(Boolean).sort();
      if (dates.length > 0) projectStartDate = dates[0];
    }
    if (!projectEndDate) {
      const dates = tasks.map(t => t.endDate || t.startDate).filter(Boolean).sort();
      if (dates.length > 0) projectEndDate = dates[dates.length - 1];
    }
  }
  
  return { title, status, description, progress, code, quotationPdfUrl, ocPdfUrl, invoicePdfUrl, projectDocuments, projectStartDate, projectEndDate, tasks, milestones, id: p.id };
}

/* Hitos: se editan en CGO (/projects/[id]) y llegan por la API pública. */
const MILESTONE_LABEL = {
  pendiente: "Pendiente",
  en_curso: "En curso",
  completado: "Completado",
};

function formatMilestoneDate(iso) {
  if (!iso || typeof iso !== "string") return null;
  const [y, m, d] = iso.split("T")[0].split("-");
  const date = new Date(y, m - 1, d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" });
}

/* Etapa que se muestra en la tarjeta: la que está en curso, o la siguiente
   pendiente después del último hito completado. */
function currentMilestone(milestones) {
  if (!milestones.length) return null;
  const active = milestones.find((m) => m.status === "en_curso");
  if (active) return active;
  const pending = milestones.find((m) => m.status !== "completado");
  return pending || milestones[milestones.length - 1];
}

function ProjectMilestones({ milestones }) {
  if (!milestones || milestones.length === 0) return null;

  return (
    <div className={styles.msContainer}>
      <ol className={styles.msList}>
        {milestones.map((m) => {
          const date = formatMilestoneDate(m.date);
          const status = MILESTONE_LABEL[m.status] ? m.status : "pendiente";
          return (
            <li key={m.id} className={styles.msItem} data-status={status}>
              <span className={styles.msDot} aria-hidden="true" />
              <div className={styles.msBody}>
                <div className={styles.msHead}>
                  <span className={styles.msTitle}>{m.title || "Hito"}</span>
                  <span className={styles.msStatus} data-status={status}>{MILESTONE_LABEL[status]}</span>
                </div>
                {date && (
                  <div className={styles.msDate}>
                    {status === "completado" ? date : `Estimado: ${date}`}
                  </div>
                )}
                {m.note && <p className={styles.msNote}>{m.note}</p>}
                {m.evidence?.length > 0 && (
                  <div className={styles.msEvidence}>
                    {m.evidence.map((f) => {
                      const isImg = f.type && f.type.includes("image");
                      const isPdf = f.type && f.type.includes("pdf");
                      return (
                        <a key={f.url} href={f.url} target="_blank" rel="noreferrer" className={styles.msFile} title={f.name}>
                          {isImg ? (
                            <img src={f.url} alt="" className={styles.msThumb} loading="lazy" />
                          ) : (
                            <span className={styles.msKind} data-kind={isPdf ? "pdf" : "file"}>{isPdf ? "PDF" : "ARCH"}</span>
                          )}
                          <span className={styles.msFileName}>{f.name}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ProjectTimeline({ startDate, endDate }) {
  if (!startDate || !endDate || typeof startDate !== 'string' || typeof endDate !== 'string') return null;

  let start, end;
  try {
    const [sYear, sMonth, sDay] = startDate.split('T')[0].split('-');
    start = new Date(sYear, sMonth - 1, sDay, 0, 0, 0);
    
    const [eYear, eMonth, eDay] = endDate.split('T')[0].split('-');
    end = new Date(eYear, eMonth - 1, eDay, 23, 59, 59);
  } catch (e) {
    return null;
  }
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const now = new Date();
  const totalMs = end - start;
  const elapsedMs = now - start;
  const progressPct = Math.max(0, Math.min(100, (elapsedMs / totalMs) * 100));

  const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
  
  // Create an array of days to render the columns
  const days = [];
  for (let i = 0; i < totalDays; i++) {
    const d = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
    const isMonday = d.getDay() === 1;
    days.push({ id: i, isMonday });
  }

  const formattedStart = start.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
  const formattedEnd = end.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });

  // Si estamos antes del inicio, darle al menos un 1% o un ancho mínimo para que se note el "punto de partida"
  const displayProgress = progressPct === 0 && now < start ? 0 : progressPct;

  return (
    <div className={styles.timelineWrapper}>
      <div className={styles.timelineHeader}>
        <span className={styles.timelineDateLabel}>F. Inicio: {formattedStart}</span>
        <span className={styles.timelineDateLabel}>F. Entrega: {formattedEnd}</span>
      </div>
      <div className={styles.timelineBars}>
        {days.map((day) => (
          <div 
            key={day.id} 
            className={`${styles.timelineDay} ${day.isMonday ? styles.timelineMonday : ''}`} 
          />
        ))}
      </div>
      <div className={styles.timelineProgressTrack}>
        <div 
          className={styles.timelineProgressBar} 
          style={{ width: `${progressPct}%`, minWidth: progressPct === 0 ? '4px' : '0' }}
        />
      </div>
    </div>
  );
}

function ProjectGantt({ tasks, startDate, endDate }) {
  const [activeBarId, setActiveBarId] = useState(null);

  if (!tasks || tasks.length === 0 || !startDate || !endDate) return null;

  let start, end;
  try {
    const [sYear, sMonth, sDay] = startDate.split('T')[0].split('-');
    start = new Date(sYear, sMonth - 1, sDay, 0, 0, 0);
    
    const [eYear, eMonth, eDay] = endDate.split('T')[0].split('-');
    end = new Date(eYear, eMonth - 1, eDay, 23, 59, 59);
  } catch (e) {
    return null;
  }

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const totalMs = end - start;
  const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
  
  // Calculate percentage positions for a given date
  const getPositionPct = (dateStr) => {
    if (!dateStr) return null;
    try {
      const [y, m, d] = dateStr.split('T')[0].split('-');
      const date = new Date(y, m - 1, d, 12, 0, 0); // Noon to avoid edge cases
      const pct = ((date - start) / totalMs) * 100;
      return Math.max(0, Math.min(100, pct));
    } catch (e) {
      return null;
    }
  };

  const now = new Date();
  let todayPct = ((now - start) / totalMs) * 100;
  let showToday = false;
  if (todayPct >= 0 && todayPct <= 100) {
    showToday = true;
  }

  // Generamos algunas marcas para las semanas (simplificado)
  const allWeeks = [];
  let current = new Date(start);
  while (current <= end) {
    if (current.getDay() === 1) { // Lunes
      const pct = ((current - start) / totalMs) * 100;
      allWeeks.push({
        pct,
        label: `${current.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit' })}`
      });
    }
    current.setDate(current.getDate() + 1);
  }
  const step = allWeeks.length > 12 ? Math.ceil(allWeeks.length / 10) : 1;
  const weeks = allWeeks.filter((_, i) => i % step === 0);

  return (
    <div className={styles.ganttContainer}>
      <h4 className={styles.sectionTitle}>Cronograma de Tareas</h4>
      <div className={styles.ganttChart}>
        <div className={styles.ganttInner}>
          {/* Encabezado del tiempo (semanas) */}
          <div className={styles.ganttHeader}>
            <div className={styles.ganttTaskColumnLabel}>TAREAS</div>
            <div className={styles.ganttTimelineArea}>
              {weeks.map((w, i) => (
                <div key={i} className={styles.ganttWeekLabel} style={{ left: `${w.pct}%` }}>
                  {w.label}
                </div>
              ))}
            </div>
          </div>

          {/* Cuerpo del Gantt */}
          <div className={styles.ganttBody}>
            {/* Líneas de cuadrícula (fondo) */}
            <div className={styles.ganttGrid}>
              {weeks.map((w, i) => (
                <div key={i} className={styles.ganttGridLine} style={{ left: `${w.pct}%` }} />
              ))}
              {showToday && (
                <div className={styles.ganttTodayLine} style={{ left: `${todayPct}%` }}>
                  <span className={styles.ganttTodayLabel}>HOY</span>
                </div>
              )}
            </div>

            {/* Filas de tareas */}
            <div className={styles.ganttRows}>
              {tasks.map((task, idx) => {
                const startPct = getPositionPct(task.startDate);
                let endPct = getPositionPct(task.endDate);
                
                if (startPct === null) return null;
                if (endPct === null) endPct = startPct + 5; // Default short width if no end

                // Asegurar que la barra tenga al menos 1% de ancho y no se desborde invertida
                const left = startPct;
                const width = Math.max(1, endPct - startPct);
                
                const isDone = task.status === 'completed' || task.status === 'done' || task.completed === true;
                const statusColor = isDone ? 'var(--color-accent)' : 'var(--color-ink-muted)';
                const taskName = task.title || task.name || 'Tarea sin nombre';

                return (
                  <div key={task.id || idx} className={styles.ganttRow}>
                    <div className={styles.ganttTaskName} title={taskName}>
                      <span className={styles.ganttTaskDot} style={{ backgroundColor: statusColor }} />
                      {taskName}
                    </div>
                    <div className={styles.ganttBarArea}>
                      <div 
                        className={styles.ganttBar} 
                        style={{ 
                          left: `${left}%`, 
                          width: `${width}%`,
                          backgroundColor: statusColor,
                          cursor: 'pointer',
                          zIndex: activeBarId === task.id ? 10 : 1
                        }}
                        onClick={() => setActiveBarId(activeBarId === task.id ? null : task.id)}
                        title={`${taskName}: ${task.startDate} al ${task.endDate || '?'}`}
                      >
                        {/* Optional: label inside bar if wide enough */}
                        {width > 15 && (
                          <span className={styles.ganttBarLabel}>
                            {task.startDate?.slice(5)} al {task.endDate?.slice(5)}
                          </span>
                        )}
                      </div>

                      {/* Tooltip que se muestra al hacer clic en la barra */}
                      {activeBarId === task.id && (
                        <div 
                          className={styles.ganttTooltip}
                          style={{ left: `${left + (width / 2)}%` }}
                        >
                          {task.startDate} al {task.endDate}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Tipo de archivo para el icono: por MIME y, si falta, por extensión. */
function fileKind(doc) {
  const type = (doc.type || "").toLowerCase();
  const ext = (doc.name || "").split(".").pop().toLowerCase();
  if (type.includes("pdf") || ext === "pdf") return "pdf";
  if (type.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif", "heic"].includes(ext)) return "img";
  if (type.startsWith("video/") || ["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return "video";
  if (type.includes("zip") || type.includes("rar") || ["zip", "rar", "7z"].includes(ext)) return "zip";
  if (type.includes("sheet") || type.includes("excel") || ["xls", "xlsx", "csv"].includes(ext)) return "sheet";
  if (type.includes("word") || ["doc", "docx"].includes(ext)) return "word";
  if (["dwg", "dxf", "step", "stp", "stl", "sldprt", "sldasm"].includes(ext)) return "cad";
  return "file";
}

const KIND_LABEL = { pdf: "PDF", img: "Imagen", video: "Video", zip: "Comprimido", sheet: "Hoja de cálculo", word: "Word", cad: "Archivo CAD", file: "Archivo" };

function DocIcon({ kind }) {
  const common = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };
  const page = <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>;
  switch (kind) {
    case "pdf":
      return <svg {...common}>{page}<path d="M8 13h1.5a1.5 1.5 0 0 1 0 3H8v-3zm0 3v2" /><path d="M13 13v5h1a2 2 0 0 0 2-2v-1a2 2 0 0 0-2-2h-1z" /></svg>;
    case "img":
      return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>;
    case "video":
      return <svg {...common}><rect x="2" y="5" width="15" height="14" rx="2" /><polygon points="22 8 17 12 22 16 22 8" /></svg>;
    case "zip":
      return <svg {...common}>{page}<path d="M10 9h1M10 12h1M10 15h1" /><rect x="9.5" y="16.5" width="2" height="2.5" rx="0.5" /></svg>;
    case "sheet":
      return <svg {...common}>{page}<path d="M8 13h8M8 17h8M12 11v8" /></svg>;
    case "word":
      return <svg {...common}>{page}<path d="M8 12l1.2 6L12 13l2.8 5L16 12" /></svg>;
    case "cad":
      return <svg {...common}><path d="M12 2l9 5v10l-9 5-9-5V7z" /><path d="M12 22V12M21 7l-9 5-9-5" /></svg>;
    case "quote":
      return <svg {...common}>{page}<path d="M8 13h8M8 17h5" /></svg>;
    case "order":
      return <svg {...common}>{page}<path d="M9 15l2 2 4-4" /></svg>;
    case "invoice":
      return <svg {...common}>{page}<path d="M12 11v7M14.5 12.5h-3.25a1.25 1.25 0 0 0 0 2.5h1.5a1.25 1.25 0 0 1 0 2.5H9.5" /></svg>;
    default:
      return <svg {...common}>{page}</svg>;
  }
}

const DOCS_VISIBLE = 6;

/* Documentos del proyecto como accesos directos, junto a la tarjeta de
   resumen. Sin scroll propio: si hay muchos, se muestran los primeros y un
   botón despliega el resto. */
function ProjectDocsCard({ project, client, formatBytes }) {
  const [showAll, setShowAll] = useState(false);
  const logistics = client?.showLogistics
    ? [
        project.quotationPdfUrl && { name: "Cotización oficial", url: project.quotationPdfUrl, kind: "quote", meta: "Logística · PDF" },
        project.ocPdfUrl && { name: "Orden de compra", url: project.ocPdfUrl, kind: "order", meta: "Logística · PDF" },
        project.invoicePdfUrl && { name: "Factura emitida", url: project.invoicePdfUrl, kind: "invoice", meta: "Logística · PDF" },
      ].filter(Boolean)
    : [];
  const files = (project.projectDocuments || []).map((d) => {
    const kind = fileKind(d);
    const date = d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" }) : null;
    return { name: d.name, url: d.url, kind, meta: [KIND_LABEL[kind], d.size ? formatBytes(d.size) : null, date].filter(Boolean).join(" · ") };
  });
  const docs = [...logistics, ...files];
  const visible = showAll ? docs : docs.slice(0, DOCS_VISIBLE);

  return (
    <aside className={styles.docsCard}>
      <div className={styles.docsHead}>
        <span className={styles.msCurrentLabel}>Documentos del proyecto</span>
        <span className={styles.tabCount}>{docs.length}</span>
      </div>
      {docs.length === 0 ? (
        <p className={styles.docsEmpty}>Aún no hay documentos. Aparecerán aquí en cuanto los subamos.</p>
      ) : (
        <ul className={styles.docsList}>
          {visible.map((d) => (
            <li key={d.url}>
              <a href={d.url} target="_blank" rel="noreferrer" className={styles.docsItem} title={d.name}>
                <span className={styles.docsIcon} data-kind={d.kind}><DocIcon kind={d.kind} /></span>
                <span className={styles.docsInfo}>
                  <span className={styles.docsName}>{d.name}</span>
                  <span className={styles.docsMeta}>{d.meta}</span>
                </span>
                <svg className={styles.docsOpen} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17L17 7" /><polyline points="8 7 17 7 17 16" /></svg>
              </a>
            </li>
          ))}
        </ul>
      )}
      {docs.length > DOCS_VISIBLE && (
        <button type="button" className={styles.docsMore} onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Ver menos" : `Ver todos (${docs.length})`}
        </button>
      )}
    </aside>
  );
}

function ReportButton({ project, client }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const generate = async (kind) => {
    setOpen(false);
    setBusy(kind);
    try {
      const report = await import("../../lib/projectReport.js");
      const opts = { client, statusLabel: STATUS_LABEL[project.status] || project.status };
      if (kind === "pdf") await report.downloadReportPdf(project, opts);
      else await report.downloadReportDocx(project, opts);
    } catch (err) {
      console.error("No se pudo generar el reporte:", err);
      alert("No se pudo generar el reporte. Intenta de nuevo.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.report} ref={ref}>
      <button type="button" className={styles.reportBtn} onClick={() => setOpen((o) => !o)} disabled={!!busy} aria-haspopup="menu" aria-expanded={open}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        {busy ? "Generando…" : "Generar reporte"}
      </button>
      {open && (
        <div className={styles.reportMenu} role="menu">
          <button type="button" role="menuitem" onClick={() => generate("pdf")}>
            <span className={styles.reportKind} data-kind="pdf">PDF</span>
            <span><strong>PDF</strong><small>A4, listo para imprimir o compartir</small></span>
          </button>
          <button type="button" role="menuitem" onClick={() => generate("docx")}>
            <span className={styles.reportKind} data-kind="docx">DOC</span>
            <span><strong>Word editable</strong><small>A4, archivo .docx para modificar</small></span>
          </button>
        </div>
      )}
    </div>
  );
}

/* Vista del proyecto: resumen y documentos lado a lado, y debajo la línea de
   tiempo y el cronograma en pestañas. Una sola barra de desplazamiento (la de
   la página). */
function ProjectDetail({ project, client, formatBytes, onBack }) {
  const done = project.milestones.filter((m) => m.status === "completado").length;
  const tabs = [
    project.milestones.length > 0 && { id: "hitos", label: "Línea de tiempo", count: `${done}/${project.milestones.length}` },
    project.tasks.length > 0 && project.projectStartDate && project.projectEndDate && { id: "cronograma", label: "Cronograma", count: project.tasks.length },
  ].filter(Boolean);
  const [tab, setTab] = useState(tabs[0]?.id || null);
  const cur = currentMilestone(project.milestones);

  return (
    <article className={styles.detail}>
      <div className={styles.detailBar}>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Mis proyectos
        </button>
        <ReportButton project={project} client={client} />
      </div>

      <div className={styles.detailTop}>
      <header className={styles.detailHead}>
        <div className={styles.cardTop}>
          {project.code && <span className={styles.code}>{project.code}</span>}
          {project.status && (
            <span className={styles.status} data-status={project.status}>
              {STATUS_LABEL[project.status] || project.status}
            </span>
          )}
        </div>
        <h2 className={styles.detailTitle}>{project.title}</h2>
        {project.description && <p className={styles.detailDesc}>{project.description}</p>}
        <div className={styles.detailStats}>
          {project.progress != null && (
            <div className={styles.progressWrap}>
              <div className={styles.progressBar}>
                <span className={styles.progressFill} style={{ width: `${project.progress}%` }} />
              </div>
              <span className={styles.progressPct}>{project.progress}%</span>
            </div>
          )}
          {cur && (
            <div className={styles.msCurrent}>
              <span className={styles.msCurrentLabel}>Etapa actual</span>
              <span className={styles.msCurrentTitle}>
                <span className={styles.msMiniDot} data-status={MILESTONE_LABEL[cur.status] ? cur.status : "pendiente"} />
                {cur.title || "Hito"}
              </span>
            </div>
          )}
        </div>
        <ProjectTimeline startDate={project.projectStartDate} endDate={project.projectEndDate} />
      </header>
      <ProjectDocsCard project={project} client={client} formatBytes={formatBytes} />
      </div>

      {tabs.length > 0 && <section className={styles.timelineCard}>
      <div className={styles.tabs} role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={styles.tab} onClick={() => setTab(t.id)}>
            {t.label}
            <span className={styles.tabCount}>{t.count}</span>
          </button>
        ))}
      </div>

      <div className={styles.tabPanel} role="tabpanel">
        {tab === "hitos" && <ProjectMilestones milestones={project.milestones} />}
        {tab === "cronograma" && (
          <ProjectGantt tasks={project.tasks} startDate={project.projectStartDate} endDate={project.projectEndDate} />
        )}
      </div>
      </section>}
    </article>
  );
}

export default function ClientDashboard() {
  const { user, client } = useAuth();
  const email = client?.email || user?.email || "";
  const { items, loading, error, reload } = useClientProjects(email);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("proyecto");
  const selectedRaw = selectedId ? items.find((p) => p.id === selectedId) : null;
  const selectedProject = selectedRaw ? readProject(selectedRaw) : null;

  // El proyecto abierto va en la URL: el botón Atrás del navegador vuelve a la lista.
  const openProject = (id) => {
    setSearchParams({ proyecto: id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const closeProject = () => setSearchParams({});

  const firstName = (client?.name || user?.displayName || "").split(" ")[0];

  // Helper function to format file sizes
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <section className={styles.wrap}>
      <header className={styles.head}>
        <span className="mono-tag">// portal de clientes</span>
        <h1 className={styles.h1}>
          {firstName ? <>Hola, {firstName}</> : <>Tus proyectos</>}
          <span className={styles.dot}>.</span>
        </h1>
        <p className={styles.lead}>
          Aquí puedes seguir el avance de los proyectos que tienes con Aya
          Technologies — estado, documentación y novedades.
        </p>
      </header>

      {loading ? (
        <p className="mono-tag">↳ cargando proyectos…</p>
      ) : error ? (
        <div className={styles.empty}>
          <p className="mono-tag">// no pudimos cargar tus proyectos</p>
          <p className={styles.emptyText}>
            Hubo un problema de conexión con el servidor. Revisa tu conexión y
            vuelve a intentarlo. Si el problema continúa, escríbenos a{" "}
            <a className={styles.link} href="mailto:ventas@ayatech.com.pe">
              ventas@ayatech.com.pe
            </a>
            .
          </p>
          <button type="button" className={styles.retry} onClick={reload}>
            Reintentar
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <p className="mono-tag">↳ aún no hay proyectos asociados a tu cuenta</p>
          <p className={styles.emptyText}>
            En cuanto registremos un proyecto a tu nombre aparecerá aquí
            automáticamente. ¿Dudas? Escríbenos a{" "}
            <a className={styles.link} href="mailto:ventas@ayatech.com.pe">
              ventas@ayatech.com.pe
            </a>
            .
          </p>
        </div>
      ) : (
        selectedProject ? (
        <ProjectDetail
          key={selectedProject.id}
          project={selectedProject}
          client={client}
          formatBytes={formatBytes}
          onBack={closeProject}
        />
      ) : (
        <ul className={styles.grid}>
          {items.map((p) => {
            const v = readProject(p);
            const cur = currentMilestone(v.milestones);
            return (
              <li key={p.id} className={styles.cardWrapper}>
                <div className={styles.card}>
                  <div className={styles.cardTop}>
                    {v.code && <span className={styles.code}>{v.code}</span>}
                    {v.status && (
                      <span className={styles.status} data-status={v.status}>
                        {STATUS_LABEL[v.status] || v.status}
                      </span>
                    )}
                  </div>

                  <h2 className={styles.cardTitle} title={v.title}>{v.title}</h2>

                  <div className={styles.cardBody}>
                    {v.description && <p className={styles.cardDesc}>{v.description}</p>}

                    {v.progress != null && (
                      <div className={styles.progressWrap}>
                        <div className={styles.progressBar}>
                          <span className={styles.progressFill} style={{ width: `${v.progress}%` }} />
                        </div>
                        <span className={styles.progressPct}>{v.progress}%</span>
                      </div>
                    )}

                    <ProjectTimeline startDate={v.projectStartDate} endDate={v.projectEndDate} />

                    {cur && (
                      <div className={styles.msCurrent}>
                        <span className={styles.msCurrentLabel}>Etapa actual</span>
                        <span className={styles.msCurrentTitle}>
                          <span className={styles.msMiniDot} data-status={MILESTONE_LABEL[cur.status] ? cur.status : "pendiente"} />
                          {cur.title || "Hito"}
                        </span>
                      </div>
                    )}

                    <button className={styles.btnDetails} onClick={() => openProject(v.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                      Ver detalles
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )
      )}
    </section>
  );
}
